/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty, isEqual, sortBy, times, toLower } from 'lodash';
import PerfectScrollbar from 'components/SafePerfectScrollbar';
import { objToArray, toString } from 'utilities';
import FundListProps from './FundList.props';
import { CompanyItem, FundItem } from '../../../../context/MDContext/types';

const StyledRootGrid = styled(Grid)({
  width: '100%',
});

const StyledListContainer = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(1),
  border: '1px solid #8A9097',
  height: 300,
  borderRadius: 5,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  '& .scrollbar-container': {
    paddingTop: 0,
  },
}));

const StyledListTitle = styled(Typography)({
  color: '#444',
  fontWeight: 600,
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)} ${theme.spacing(0.5)} !important`,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#8A9097 !important',
  },
  '& .MuiInputBase-adornedEnd': {
    paddingRight: `${theme.spacing(1)}px !important`,
  },
}));

const StyledSearchIcon = styled(SearchIcon)({
  color: '#8A9097',
});

const FundList = (props: FundListProps) => {
  const {
    items,
    fundList,
    isLoading,
    selectedFunds,
    selectedCompanies,
    setSelectedCompanies,
    setSelectedFunds,
    isDeletingMD,
  } = props;

  const [filteredItems, setFilteredItems] = useState<FundItem[]>([]);

  const fundItems = useMemo<FundItem[]>(() => {
    if (items) {
      return items;
    }
    if (fundList) {
      return fundList.map(({ id, fund }) => ({ id: Number(id), name: fund.name, subItems: [] as CompanyItem[] }));
    }
    return [] as FundItem[];
  }, [items, fundList]);

  useEffect(() => {
    if (!isEmpty(selectedFunds) && !isDeletingMD) {
      let affectedCompanies: { [key: number]: boolean } = {};

      fundItems.forEach(item => {
        if (item.id && selectedFunds[item.id] && item.subItems) {
          item.subItems.forEach(company => {
            affectedCompanies = {
              ...affectedCompanies,
              [company.id]: selectedCompanies[Number(company.id)] || true,
            };
          });
        }
      });

      setSelectedCompanies({
        ...selectedCompanies,
        ...affectedCompanies,
      });
    }
  }, [selectedFunds, fundItems]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filter = e.target.value;
    const tmpFilteredItems = fundItems.filter(item => item.id && toLower(item.name).includes(toLower(filter)));

    setFilteredItems(tmpFilteredItems);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, option: FundItem) => {
    const { checked } = e.target;

    let tmpSelectedFunds = {
      ...selectedFunds,
    };

    if (checked && option.id) {
      tmpSelectedFunds = {
        ...tmpSelectedFunds,
        [option.id]: selectedFunds[option.id] || checked,
      };
    } else if (option.id) {
      delete tmpSelectedFunds[option.id];
    }

    setSelectedFunds(tmpSelectedFunds);

    if (!isDeletingMD) {
      setSelectedFunds(tmpSelectedFunds);
      const affectedCompanies = fundItems.reduce(
        (filtered, item) => {
          if (toString(option.id) === toString(item.id) && item.subItems) {
            item.subItems.forEach(company => {
              if (checked) {
                filtered = {
                  ...filtered,
                  [company.id]: checked,
                };
              } else {
                delete filtered[Number(company.id)];
              }
            });
          }

          return filtered;
        },
        { ...selectedCompanies }
      );
      setSelectedCompanies(affectedCompanies);
    }
  };

  const isChecked = useCallback(
    (id: string) => {
      if (selectedFunds?.[id]) {
        return true;
      }

      const tmpSelectedItems = { ...selectedCompanies };
      const { subItems: companies } = fundItems.find(item => toString(item.id) === toString(id)) || {};
      const availableCompanyIds = companies ? companies.map(({ id }) => toString(id)) : [];

      const selectedCompanyIds = objToArray(tmpSelectedItems).reduce((filtered, [companyId, selected]) => {
        if (selected && availableCompanyIds.includes(companyId)) {
          filtered.push(toString(companyId));
        }
        return filtered;
      }, [] as string[]);

      const allSelected = availableCompanyIds.length === selectedCompanyIds.length;
      return availableCompanyIds.length > 0 && allSelected;
    },
    [selectedFunds, selectedCompanies, fundItems]
  );

  const isIndeterminate = useCallback(
    (id: string) => {
      if (Array.isArray(fundItems)) {
        const fundItem = fundItems?.find(fund => toString(fund.id) === toString(id)) ?? undefined;

        if (fundItem) {
          const { subItems: companies } = fundItem;
          const availabeCompanyIds = companies?.map(({ id: companyId }) => toString(companyId)) ?? [];

          const selectedCompanyIds = objToArray(selectedCompanies).reduce((filtered, [id, selected]) => {
            if (selected && availabeCompanyIds.includes(id)) {
              filtered.push(toString(id));
            }
            return filtered;
          }, [] as string[]);

          if (selectedCompanyIds.length) {
            return !isEqual(sortBy(selectedCompanyIds), sortBy(availabeCompanyIds));
          }
        }
        return false;
      }
      return false;
    },
    [fundItems, selectedCompanies]
  );

  const renderItems = () =>
    (filteredItems || []).map(item => (
      <FormControl key={`checkbox-${item.id}`} component="fieldset">
        <FormGroup>
          <FormControlLabel
            key={item.id}
            control={
              <Checkbox
                id={`checkbox-${item.id}`}
                size="small"
                name={item.name}
                checked={isChecked(item.id?.toString() || '')}
                indeterminate={isIndeterminate(item.id?.toString() || '')}
                onChange={e => handleChange(e, item)}
              />
            }
            label={item.name}
          />
        </FormGroup>
      </FormControl>
    ));

  const renderSkeleton = () => {
    const rows = 4;
    return times(rows, key => <Skeleton key={key} variant="text" height={40} />);
  };

  useEffect(() => {
    if (!isEqual(fundItems, filteredItems)) {
      const tmpItems = fundItems.filter(fund => fund.id) || [];
      setFilteredItems(tmpItems);
    }
  }, [fundItems]);

  return (
    <StyledRootGrid>
      <StyledListTitle variant="subtitle2" gutterBottom>
        Select one or more Funds:
      </StyledListTitle>
      <StyledTextField
        fullWidth
        variant="outlined"
        placeholder="Find a Fund"
        onChange={handleFilterChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <StyledSearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <StyledListContainer>
        <PerfectScrollbar options={{ minScrollbarLength: 50 }}>
          <Grid container direction="column" wrap="nowrap">
            {Array.isArray(filteredItems) && !isLoading ? renderItems() : renderSkeleton()}
          </Grid>
        </PerfectScrollbar>
      </StyledListContainer>
    </StyledRootGrid>
  );
};

export default FundList;